import { GapDensityMapThemeContext } from "@app/projects/network-assessment/maps/gap-density-map/contexts/GapDensityMapThemeContext";
import { GapDensityMapColorComponent } from "@app/projects/network-assessment/maps/gap-density-map/components/GapDensityMapColorComponent";
import { GAP_DENSITY_MAP_STYLE, gapDensityMapThemeDefault } from "@app/projects/network-assessment/maps/gap-density-map/GapDensityMapsConstants";
import { GapDensityMapThemeComponent } from "@app/projects/network-assessment/maps/gap-density-map/components/GapDensityMapThemeComponent";
import { FC, useContext, useMemo, useState, useEffect } from "react";
import { Modal, ModalContent } from "@app/shared/Modals";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";

interface GapDensityMapEditThemesFlyoutProps {
  onDirtyChange?: (dirty: boolean) => void;
  warningModal?: Partial<ModalContent>;
}
export const GapDensityMapEditThemesFlyout: FC<GapDensityMapEditThemesFlyoutProps> = ({ onDirtyChange, warningModal }) => {

  const themeContext = useContext(GapDensityMapThemeContext);
  const [isDirty, setIsDirty] = useState(false);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [initialTheme, setInitialTheme] = useState(themeContext.themeToEdit);

  const isDuplicateCustomThemeName = useMemo(() => {
    return !!themeContext.customThemes.find(
      theme =>
        theme?.name.toLowerCase() === themeContext.themeToEdit?.name.toLowerCase() &&
        theme?.uuid !== themeContext.themeToEdit?.uuid
    );
  }, [themeContext.customThemes, themeContext.themeToEdit]);

  const isDuplicateQuestThemeName = useMemo(() =>
      !!GAP_DENSITY_MAP_STYLE.QUEST_THEMES.find(theme =>
        theme?.name.toLowerCase() === themeContext.themeToEdit?.name.toLowerCase()),
    [themeContext.themeToEdit]);

  useEffect(() => {
    if (themeContext.themeToEdit && !initialTheme) {
      setInitialTheme(themeContext.themeToEdit);
    }
    if (!themeContext.themeToEdit) {
      setInitialTheme(null);
      setIsDirty(false);
    }
  }, [themeContext.themeToEdit]);

  useEffect(() => {
    if (themeContext.themeToEdit && initialTheme) {
      setIsDirty(JSON.stringify(themeContext.themeToEdit) !== JSON.stringify(initialTheme));
    }
  }, [themeContext.themeToEdit, initialTheme]);

  useEffect(() => {
    if (typeof onDirtyChange === "function") {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  return (
    <>
      <div>
        <p><strong>Custom Themes</strong></p>
        {themeContext.customThemes?.length === 0 &&
          <QESAlert
            type="info"
            persist={true}
          >
            No custom themes available.
          </QESAlert>
        }
        {
          themeContext.customThemes?.map(theme => (
            <GapDensityMapThemeComponent key={theme.name} theme={theme} />
          ))
        }
        {!themeContext.themeToEdit && (
          <QESButton
            qesType="secondary"
            qesTestId="gap-density-map__theme--create-new-theme"
            onClick={() => {
              themeContext.setThemeToEdit(gapDensityMapThemeDefault());
              themeContext.setEditingBucketIndex(null);
            }}
          >
            Create new theme
          </QESButton>
        )}
        {!!themeContext.themeToEdit && (
          <>
            <p>Gap ranges are dynamic to the specialty count of the project</p>
            <QESTextInput
              className="gap-density-map__theme-name"
              required={true}
              label={"Theme Name:"}
              testId="gap-density-map__theme--theme-name-input"
              value={themeContext.themeToEdit?.name}
              descriptiveText={
                (() => {
                  if (!themeContext.themeToEdit?.name) {
                    return "Please provide a theme name.";
                  } else if (isDuplicateCustomThemeName) {
                    return "Theme name already exists. Please choose a different name.";
                  } else if (isDuplicateQuestThemeName) {
                    return "This name is reserved for a system theme. Please choose a different name.";
                  } else {
                    return "";
                  }
                })()
              }
              isValid={(!isDuplicateCustomThemeName && !isDuplicateQuestThemeName && !!themeContext.themeToEdit?.name)}
              placeholder="Enter theme name"
              onChange={(event) => {
                themeContext.setThemeToEdit({
                  ...themeContext.themeToEdit,
                  name: event.target.value
                });
              }}
            />
            <GapDensityMapColorComponent bucket={0}/>
            <GapDensityMapColorComponent bucket={1}/>
            <GapDensityMapColorComponent bucket={2}/>
            <GapDensityMapColorComponent bucket={3}/>
            <GapDensityMapColorComponent bucket={4}/>
            <QESButton
              qesType="secondary"
              qesTestId="gap-density-map__theme--save-new-theme"
              onClick={() => {
                themeContext.addOrUpdateCustomTheme(themeContext.themeToEdit);
                themeContext.setThemeToEdit(null);
                themeContext.setEditingBucketIndex(null);
              }}
              disabled={
                !themeContext.themeToEdit?.name ||
                !!themeContext.customThemes?.find(theme => theme === themeContext.themeToEdit) ||
                !!isDuplicateCustomThemeName ||
                !!isDuplicateQuestThemeName
              }
            >
              Save
            </QESButton>
            <QESButton
              qesType="secondary-danger"
              qesTestId="gap-density-map__theme--cancel-new-theme"
              onClick={() => {
                if (isDirty) {
                  setIsCancelModalVisible(isDirty);
                } else {
                  themeContext.setThemeToEdit(null);
                  themeContext.setEditingBucketIndex(null);
                }
              }}
            >
              Cancel
            </QESButton>
          </>
        )}
      </div>
      <Modal
        content={{
          body: warningModal?.body || undefined,
          cancelButtonText: warningModal?.cancelButtonText || undefined,
          confirmButtonAction: () => {
            themeContext.setThemeToEdit(null);
            themeContext.setEditingBucketIndex(null);
            setIsCancelModalVisible(false);
          },
          confirmButtonText: warningModal?.confirmButtonText || undefined,
          title: warningModal?.title || undefined,
        }}
        closeModal={() => setIsCancelModalVisible(false)}
        isVisible={isCancelModalVisible}
      />
    </>
  );
};
